import { User } from "firebase/auth";
import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { analyticsService } from "../services/AnalyticsService";
import { createLogger } from "../utils/logger";

const logger = createLogger("useAnalytics");

// Global page view tracking to prevent duplicates
let lastTrackedPath = "";
let pageViewTimeout: NodeJS.Timeout | null = null;

/**
 * React hook for analytics functionality
 * Provides easy access to analytics methods and automatic page view tracking
 */
export const useAnalytics = (user?: User | null) => {
  const location = useLocation();

  // Set user when it changes
  useEffect(() => {
    if (user !== undefined) {
      analyticsService.setUser(user);
    }
  }, [user]);

  // Track page views on route changes (with global deduplication)
  useEffect(() => {
    const pageTitle = document.title || "Costaff.ai";
    const pagePath = location.pathname + location.search;

    // Skip analytics tracking for OAuth callback URLs to prevent page reloads
    if (pagePath.includes("/auth/google/callback")) {
      logger.debug("Skipping analytics tracking for OAuth callback URL:", pagePath);
      return;
    }

    // Clear any existing timeout
    if (pageViewTimeout) {
      clearTimeout(pageViewTimeout);
    }

    // Only track if this is a new path or after a delay
    if (lastTrackedPath !== pagePath) {
      pageViewTimeout = setTimeout(() => {
        analyticsService.logPageView(pageTitle, pagePath);
        logger.debug("Page view tracked:", pagePath);
        lastTrackedPath = pagePath;
        pageViewTimeout = null;
      }, 200);
    }
  }, [location]);

  // Memoized analytics methods
  const logEvent = useCallback((event: any) => {
    analyticsService.logEvent(event);
  }, []);

  const logError = useCallback(
    (
      errorCode: string,
      errorMessage: string,
      options?: {
        errorStack?: string;
        component?: string;
        userAction?: string;
        severity?: "low" | "medium" | "high" | "critical";
      }
    ) => {
      analyticsService.logError(errorCode, errorMessage, options);
    },
    []
  );

  const logAuthEvent = useCallback((eventType: "login" | "logout" | "register" | "login_failed" | "register_failed", method: "email" | "google" | "unknown", errorCode?: string) => {
    analyticsService.logAuthEvent(eventType, method, errorCode);
  }, []);

  const logUserInteraction = useCallback((interactionType: string, component: string, action: string, value?: string | number) => {
    analyticsService.logUserInteraction(interactionType, component, action, value);
  }, []);

  const logOnboardingEvent = useCallback((step: string, action: "started" | "completed" | "skipped" | "failed", stepNumber?: number, totalSteps?: number, errorCode?: string) => {
    analyticsService.logOnboardingEvent(step, action, stepNumber, totalSteps, errorCode);
  }, []);

  const logChatEvent = useCallback(
    (
      eventType: "message_sent" | "message_received" | "session_started" | "session_ended" | "agent_selected",
      options?: {
        agentId?: string;
        messageLength?: number;
        responseTime?: number;
        errorCode?: string;
      }
    ) => {
      analyticsService.logChatEvent(eventType, options);
    },
    []
  );

  const logCalendarEvent = useCallback(
    (
      eventType: "event_created" | "event_updated" | "event_deleted" | "calendar_synced" | "view_changed",
      options?: {
        eventDuration?: number;
        calendarType?: string;
        errorCode?: string;
      }
    ) => {
      analyticsService.logCalendarEvent(eventType, options);
    },
    []
  );

  const logTodoEvent = useCallback(
    (
      eventType: "todo_created" | "todo_completed" | "todo_updated" | "todo_deleted" | "priority_set",
      options?: {
        todoCategory?: string;
        priorityLevel?: string;
        errorCode?: string;
      }
    ) => {
      analyticsService.logTodoEvent(eventType, options);
    },
    []
  );

  const logTutorialEvent = useCallback(
    (
      eventType: "tutorial_started" | "tutorial_completed" | "tutorial_skipped" | "tutorial_restarted",
      options: {
        tutorialId: string;
        tutorialName?: string;
        stepCount?: number;
        completionType?: "seen" | "completed" | "skipped";
        skipType?: "all_tutorials" | "single_tutorial";
      }
    ) => {
      analyticsService.logTutorialEvent(eventType, options);
    },
    []
  );

  const setUserProperty = useCallback((key: string, value: string | number | boolean) => {
    analyticsService.setUserProperty(key, value);
  }, []);

  const isEnabled = useCallback(() => {
    return analyticsService.isAnalyticsEnabled();
  }, []);

  return {
    logEvent,
    logError,
    logAuthEvent,
    logUserInteraction,
    logOnboardingEvent,
    logChatEvent,
    logCalendarEvent,
    logTodoEvent,
    logTutorialEvent,
    setUserProperty,
    isEnabled,
  };
};

/**
 * Hook for tracking component errors with analytics
 */
export const useErrorTracking = (componentName: string) => {
  const { logError } = useAnalytics();

  const trackError = useCallback(
    (error: Error, userAction?: string, severity: "low" | "medium" | "high" | "critical" = "medium") => {
      logError("COMPONENT_ERROR", error.message, {
        errorStack: error.stack,
        component: componentName,
        userAction,
        severity,
      });
    },
    [logError, componentName]
  );

  return { trackError };
};

/**
 * Hook for tracking user interactions in a specific component
 */
export const useInteractionTracking = (componentName: string) => {
  const { logUserInteraction } = useAnalytics();

  const trackClick = useCallback(
    (action: string, value?: string | number) => {
      logUserInteraction("click", componentName, action, value);
    },
    [logUserInteraction, componentName]
  );

  const trackFormSubmit = useCallback(
    (formName: string, success: boolean) => {
      logUserInteraction("form_submit", componentName, formName, success ? 1 : 0);
    },
    [logUserInteraction, componentName]
  );

  const trackView = useCallback(
    (viewName: string) => {
      logUserInteraction("view", componentName, viewName);
    },
    [logUserInteraction, componentName]
  );

  const trackCustom = useCallback(
    (interactionType: string, action: string, value?: string | number) => {
      logUserInteraction(interactionType, componentName, action, value);
    },
    [logUserInteraction, componentName]
  );

  return {
    trackClick,
    trackFormSubmit,
    trackView,
    trackCustom,
  };
};

export default useAnalytics;
